/**
 * motoriAiService — il registro dei modelli AI (`platform_config/modelli_ai`) visto dalla
 * console, in SOLA LETTURA.
 *
 * Nessuno sceglie un modello da qui, ed è una decisione, non una mancanza (Simone,
 * 17/09/2026): il registro sceglie da solo il definitivo più recente di ogni classe, ripiega se
 * un modello viene ritirato, promuove il provvisorio quando esce il definitivo. Questa pagina
 * serve a VEDERE: chi risponde per ogni compito, da quando, perché, se il controllo del mattino
 * l'ha trovato vivo, e cosa è cambiato da solo. La forma del documento è in
 * `functions/src/_shared/ai/modelli.ts`; qui si legge con tolleranza (campi assenti = «—»).
 */

import { doc, onSnapshot, type Timestamp } from 'firebase/firestore';
import { db } from '../firebase';

export type Famiglia = 'immagine' | 'testo' | 'trascrizione' | 'agente' | 'voce_realtime';
export type Ruolo = 'qualita' | 'bilanciato' | 'economico';

export interface VoceRegistro {
  fornitore?: 'google' | 'deepgram' | 'anthropic' | 'openai';
  modello: string;
  regione: string;
  scelto_il: string;
  scelto_da: 'seed' | 'mano' | 'ripiego' | 'canarino' | 'promozione';
  ritiro_previsto?: string | null;
  motivo?: string;
  provvisorio?: boolean;
  verificato_il?: string;
  latenza_ms?: number;
}

export interface RigaStorico {
  quando: string;
  ruolo: Ruolo;
  da: string | null;
  a: string;
  perche: 'seed' | 'ripiego' | 'promozione' | 'mano' | string;
  motivo?: string;
}

export interface ProvaDelMattino {
  famiglia: Famiglia;
  ruolo: Ruolo;
  modello: string;
  regione: string;
  ok: boolean;
  ms?: number;
  ripiegato?: boolean;
  errore?: string;
}

export interface ControlloDelMattino {
  quando: Timestamp | null;
  catalogo_voci: number;
  prove: ProvaDelMattino[];
  avvisi: string[];
  email_inviata: boolean;
}

export interface DocumentoRegistro {
  controllo?: { ultimo?: Partial<ControlloDelMattino> & { quando?: Timestamp } };
  [famiglia: string]: unknown;
}

export const FAMIGLIE: readonly Famiglia[] = ['agente', 'testo', 'immagine', 'trascrizione', 'voce_realtime'];
export const RUOLI: readonly Ruolo[] = ['qualita', 'bilanciato', 'economico'];

export const NOME_FAMIGLIA: Record<Famiglia, string> = {
  agente: 'Assistenti con strumenti',
  testo: 'Lettura, scrittura e foto',
  immagine: 'Fotomontaggi e immagini',
  trascrizione: 'Trascrizione della voce',
  voce_realtime: 'Voce in tempo reale nelle app',
};
export const COSA_FA_LA_FAMIGLIA: Record<Famiglia, string> = {
  agente: 'Yuma nel gestionale (qualità), il Sole del sito e il suo controllo notturno (bilanciato), l\'agente WhatsApp (economico): Claude di Anthropic',
  testo: 'analisi dei preventivi concorrenti, lettura di bollette e documenti, chiamate, fatture, formazione, note',
  immagine: 'il fotomontaggio degli infissi e le immagini per il marketing',
  trascrizione: 'la dettatura a Yuma, le note vocali, i vocali WhatsApp, le chiamate',
  voce_realtime: 'l\'assistente vocale di YumaCALL e l\'agente della formazione (OpenAI Realtime): l\'account è fermo, la voce del registro è pronta per quando riparte',
};
export const NOME_RUOLO: Record<Ruolo, string> = { qualita: 'qualità', bilanciato: 'bilanciato', economico: 'veloce ed economico' };
export const NOME_FORNITORE: Record<string, string> = { google: 'Google', deepgram: 'Deepgram', anthropic: 'Anthropic', openai: 'OpenAI' };
export const NOME_SCELTA: Record<VoceRegistro['scelto_da'], string> = {
  seed: 'scelto dal catalogo',
  mano: 'scelto a mano',
  ripiego: 'ripiego automatico',
  canarino: 'controllo del mattino',
  promozione: 'promosso a definitivo',
};
export const NOME_PERCHE: Record<string, string> = {
  seed: 'semina',
  ripiego: 'ripiego',
  promozione: 'promozione',
  mano: 'a mano',
  // 17/10/2026: una chiave ha rifiutato (credito finito o non valida) e si è passati alla riserva; Da/A sono i nomi dei segreti.
  cambio_chiave: 'chiave di riserva',
};

export interface RigaRegistro {
  famiglia: Famiglia;
  ruolo: Ruolo;
  voce: VoceRegistro;
}

/** Le voci del registro, nell'ordine in cui si mostrano: per famiglia, qualità prima di economico. PURA. */
export function righeDelRegistro(documento: DocumentoRegistro | null | undefined): RigaRegistro[] {
  if (!documento) return [];
  const righe: RigaRegistro[] = [];
  for (const famiglia of FAMIGLIE) {
    const ruoli = (documento[famiglia] as { ruoli?: Partial<Record<Ruolo, VoceRegistro>> } | undefined)?.ruoli ?? {};
    for (const ruolo of RUOLI) {
      const voce = ruoli[ruolo];
      if (voce?.modello) righe.push({ famiglia, ruolo, voce });
    }
  }
  return righe;
}

/** Lo storico di tutte le famiglie insieme, dal più recente. PURA. */
export function storicoUnito(documento: DocumentoRegistro | null | undefined): Array<RigaStorico & { famiglia: Famiglia }> {
  if (!documento) return [];
  const righe: Array<RigaStorico & { famiglia: Famiglia }> = [];
  for (const famiglia of FAMIGLIE) {
    const storico = (documento[famiglia] as { storico?: RigaStorico[] } | undefined)?.storico ?? [];
    for (const r of storico) if (r?.quando && r?.a) righe.push({ ...r, famiglia });
  }
  return righe.sort((x, y) => (x.quando < y.quando ? 1 : x.quando > y.quando ? -1 : 0));
}

/** Giorni da oggi alla data di ritiro (YYYY-MM-DD); null se non indicata o malformata. PURA. */
export function giorniAlRitiro(ritiro: string | null | undefined, oggi: Date): number | null {
  if (!ritiro) return null;
  const a = Date.parse(`${ritiro}T00:00:00Z`);
  if (Number.isNaN(a)) return null;
  const inizio = Date.UTC(oggi.getUTCFullYear(), oggi.getUTCMonth(), oggi.getUTCDate());
  return Math.round((a - inizio) / 86_400_000);
}

export type Salute = 'vivo' | 'guasto' | 'mai_provato';

/** La salute di una voce secondo l'ultimo controllo del mattino. PURA. */
export function saluteDellaVoce(riga: RigaRegistro, controllo: Partial<ControlloDelMattino> | undefined): { salute: Salute; ms?: number; errore?: string } {
  const prova = controllo?.prove?.find((p) => p.famiglia === riga.famiglia && p.ruolo === riga.ruolo);
  if (!prova) return riga.voce.verificato_il ? { salute: 'vivo', ms: riga.voce.latenza_ms } : { salute: 'mai_provato' };
  return prova.ok ? { salute: 'vivo', ms: prova.ms } : { salute: 'guasto', errore: prova.errore };
}

/** Osserva il registro dal vivo. Restituisce la funzione per smettere. */
export function osservaRegistroModelli(
  suDocumento: (documento: DocumentoRegistro | null) => void,
  suErrore: (messaggio: string) => void,
): () => void {
  return onSnapshot(
    doc(db, 'platform_config', 'modelli_ai'),
    (snap) => suDocumento(snap.exists() ? (snap.data() as DocumentoRegistro) : null),
    (e) => suErrore(e.message),
  );
}
